import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import FaceCapture from '../components/FaceCapture';
import api from '../api/client';
import { Users, Plus, Pencil, Trash, ArrowsDownUp, CheckCircle, Clock, CurrencyDollar, UsersThree, CalendarDots, Receipt } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

function Students() {
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedBatchFilter, setSelectedBatchFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name_asc');
  const [viewMode, setViewMode] = useState('list');
  const [editFaceData, setEditFaceData] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState({ paid_student_ids: [], tutor_batch_ids: [], absent_student_ids: [], month: '', year: '' });
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [historyData, setHistoryData] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const userRole = localStorage.getItem('role') || 'admin';
  const showFees = userRole === 'admin';
  const [formData, setFormData] = useState({
    name: '',
    date_of_birth: '',
    joining_date: '',
    email: '',
    phone: '',
    batch_id: '',
    parent_name: '',
    parent_phone: '',
  });
  const [faceData, setFaceData] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchStudents();
    fetchBatches();
    if (showFees) fetchPaymentStatus();
  }, []);

  const fetchStudents = async () => {
    try {
      const response = await api.get('/students');
      setStudents(response.data);
    } catch (error) {
      toast.error('Failed to fetch students');
    } finally {
      setLoading(false);
    }
  };

  const fetchBatches = async () => {
    try {
      const response = await api.get('/batches');
      setBatches(response.data);
    } catch (error) {
      toast.error('Failed to fetch batches');
    }
  };

  const fetchStudentHistory = async (studentId) => {
    setHistoryLoading(true);
    setIsHistoryOpen(true);
    try {
      const response = await api.get(`/students/history/${studentId}`);
      setHistoryData(response.data);
    } catch (error) {
      toast.error('Failed to load student history');
    } finally {
      setHistoryLoading(false);
    }
  };

  const fetchPaymentStatus = async () => {
    try {
      const response = await api.get('/students/payment-status');
      setPaymentStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch payment status');
    }
  };

  const handleFaceCapture = (data) => {
    setFaceData(data);
    toast.success('Face captured successfully!');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!faceData) {
      toast.error('Please capture face first');
      return;
    }
    try {
      await api.post('/students', {
        ...formData,
        face_descriptor: faceData.descriptor,
        photo_url: faceData.photo,
      });
      toast.success('Student registered successfully');
      setIsDialogOpen(false);
      setFormData({ name: '', date_of_birth: '', joining_date: '', email: '', phone: '', batch_id: '', parent_name: '', parent_phone: '' });
      setFaceData(null);
      fetchStudents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to register student');
    }
  };

  const getFilteredStudents = () => {
    let filtered = selectedBatchFilter === 'all' 
      ? [...students] 
      : students.filter(s => s.batch_id === selectedBatchFilter);
    
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(s => 
        (s.name || '').toLowerCase().includes(q) ||
        (s.parent_name || '').toLowerCase().includes(q) ||
        (s.phone || '').includes(q) ||
        (s.parent_phone || '').includes(q)
      );
    }
    
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name_asc':
          return (a.name || '').localeCompare(b.name || '');
        case 'name_desc':
          return (b.name || '').localeCompare(a.name || '');
        case 'age_asc': {
          const ageA = a.date_of_birth ? calculateAge(a.date_of_birth) : 999;
          const ageB = b.date_of_birth ? calculateAge(b.date_of_birth) : 999;
          return (typeof ageA === 'number' ? ageA : 999) - (typeof ageB === 'number' ? ageB : 999);
        }
        case 'age_desc': {
          const ageA2 = a.date_of_birth ? calculateAge(a.date_of_birth) : -1;
          const ageB2 = b.date_of_birth ? calculateAge(b.date_of_birth) : -1;
          return (typeof ageB2 === 'number' ? ageB2 : -1) - (typeof ageA2 === 'number' ? ageA2 : -1);
        }
        case 'fees_pending': {
          const paidIds = paymentStatus.paid_student_ids || [];
          const absentIds = paymentStatus.absent_student_ids || [];
          const aIsPending = !paidIds.includes(a.id) && !absentIds.includes(a.id) ? 0 : 1;
          const bIsPending = !paidIds.includes(b.id) && !absentIds.includes(b.id) ? 0 : 1;
          return aIsPending - bIsPending || (a.name || '').localeCompare(b.name || '');
        }
        default:
          return 0;
      }
    });
    return filtered;
  };

  const getSiblingGroups = () => {
    let filtered = selectedBatchFilter === 'all' 
      ? [...students] 
      : students.filter(s => s.batch_id === selectedBatchFilter);
    
    const phoneGroups = {};
    const noPhone = [];
    
    filtered.forEach(s => {
      const phone = (s.parent_phone || '').trim();
      if (phone) {
        if (!phoneGroups[phone]) phoneGroups[phone] = [];
        phoneGroups[phone].push(s);
      } else {
        noPhone.push(s);
      }
    });
    
    const groups = [];
    Object.entries(phoneGroups).forEach(([phone, members]) => {
      if (members.length >= 2) {
        members.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        groups.push({ phone, members, isSibling: true });
      } else {
        noPhone.push(...members);
      }
    });
    
    noPhone.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    groups.sort((a, b) => a.members[0].name.localeCompare(b.members[0].name));
    
    return { siblingGroups: groups, individuals: noPhone };
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    try {
      const updateData = {
        name: formData.name,
        date_of_birth: formData.date_of_birth,
        joining_date: formData.joining_date,
        email: formData.email,
        phone: formData.phone,
        batch_id: formData.batch_id,
        parent_name: formData.parent_name,
        parent_phone: formData.parent_phone,
      };
      if (editFaceData) {
        updateData.face_descriptor = editFaceData.descriptor;
        updateData.photo_url = editFaceData.photo;
      }
      await api.put(`/students/${selectedStudent.id}`, updateData);
      toast.success('Student updated successfully');
      setIsEditDialogOpen(false);
      setSelectedStudent(null);
      setEditFaceData(null);
      fetchStudents();
    } catch (error) {
      toast.error('Failed to update student');
    }
  };

  const handleEditFaceCapture = (data) => {
    setEditFaceData(data);
    toast.success('New photo captured!');
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this student?')) {
      try {
        await api.delete(`/students/${id}`);
        toast.success('Student deleted successfully');
        fetchStudents();
      } catch (error) {
        toast.error('Failed to delete student');
      }
    }
  };

  const openEditDialog = (student) => {
    setSelectedStudent(student);
    setEditFaceData(null);
    setFormData({
      name: student.name,
      date_of_birth: student.date_of_birth || '',
      joining_date: student.joining_date || '',
      email: student.email,
      phone: student.phone,
      batch_id: student.batch_id,
      parent_name: student.parent_name || '',
      parent_phone: student.parent_phone || '',
    });
    setIsEditDialogOpen(true);
  };

  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return '-';
    const today = new Date();
    const dob = new Date(dateOfBirth);
    let age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
      age--;
    }
    return age;
  };

  const getBatchName = (batchId) => {
    const batch = batches.find((b) => b.id === batchId);
    return batch ? batch.name : 'Unknown';
  };

  const isStudentTutor = (student) => {
    return student.is_tutor || (paymentStatus.tutor_batch_ids || []).includes(student.batch_id);
  };

  const isStudentAbsent = (studentId) => {
    return (paymentStatus.absent_student_ids || []).includes(studentId);
  };

  const getSiblingMap = () => {
    const phoneGroups = {};
    students.forEach(s => {
      const phone = (s.parent_phone || s.phone || '').trim();
      if (phone) {
        if (!phoneGroups[phone]) phoneGroups[phone] = [];
        phoneGroups[phone].push(s);
      }
    });
    const siblingMap = {};
    Object.values(phoneGroups).forEach(group => {
      if (group.length >= 2) {
        group.forEach(s => {
          siblingMap[s.id] = group.filter(g => g.id !== s.id).map(g => g.name);
        });
      }
    });
    return siblingMap;
  };

  const siblingMap = getSiblingMap();

  const isStudentPaid = (studentId) => {
    return (paymentStatus.paid_student_ids || []).includes(studentId);
  };

  const handleRecordPayment = (student) => {
    navigate('/payments', { state: { prefillStudent: student.id } });
  };

  const renderStudentRow = (student, index) => (
    <tr key={student.id} data-testid={`student-row-${student.id}`} className="hover:bg-slate-800/50">
      <td className="px-4 py-4 text-slate-400 font-medium text-sm">{index + 1}</td>
      <td className="px-4 py-4">
        {student.photo_url ? (
          <div className="w-10 h-10 rounded-full bg-blue-600/20 flex items-center justify-center border-2 border-slate-700">
            <span className="text-blue-400 font-bold text-sm">{student.name?.charAt(0)?.toUpperCase()}</span>
          </div>
        ) : (
          <div className="w-10 h-10 rounded-full bg-blue-600/20 flex items-center justify-center">
            <Users size={20} weight="duotone" className="text-blue-400" />
          </div>
        )}
      </td>
      <td className="px-4 py-4">
        <div>
          <button
            onClick={() => fetchStudentHistory(student.id)}
            data-testid={`student-name-${student.id}`}
            className="text-slate-50 font-medium hover:text-blue-400 transition-colors cursor-pointer text-left"
          >
            {student.name}
          </button>
          {siblingMap[student.id] && (
            <div className="mt-1 flex items-center gap-1">
              <UsersThree size={12} weight="bold" className="text-purple-400" />
              <span className="text-xs text-purple-400">
                Sibling: {siblingMap[student.id].join(', ')}
              </span>
            </div>
          )}
        </div>
      </td>
      <td className="px-4 py-4 text-slate-400">{calculateAge(student.date_of_birth)}</td>
      <td className="px-4 py-4">
        <span className="px-2 py-1 bg-blue-600/20 text-blue-400 rounded-full text-xs font-medium">
          {getBatchName(student.batch_id)}
        </span>
      </td>
      <td className="px-4 py-4">
        <div className="text-sm text-slate-400">
          <div>{student.phone}</div>
          <div className="text-xs text-slate-500">{student.email}</div>
        </div>
      </td>
      {showFees && (
        <td className="px-4 py-4">
          {isStudentTutor(student) ? (
            <span className="text-xs text-slate-500 italic">Tutor</span>
          ) : isStudentAbsent(student.id) ? (
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-rose-600/20 text-rose-400 rounded-full text-xs font-medium">
              Absent
            </span>
          ) : isStudentPaid(student.id) ? (
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-600/20 text-emerald-400 rounded-full text-xs font-medium">
              <CheckCircle size={12} weight="bold" />
              Paid
            </span>
          ) : (
            <button
              onClick={() => handleRecordPayment(student)}
              data-testid={`record-payment-${student.id}`}
              className="inline-flex items-center gap-1 px-2 py-1 bg-amber-600/20 text-amber-400 hover:bg-amber-600/30 rounded-full text-xs font-medium transition-colors cursor-pointer"
            >
              <Clock size={12} weight="bold" />
              Pending
            </button>
          )}
        </td>
      )}
      <td className="px-4 py-4">
        <div className="flex items-center gap-1">
          <button onClick={() => openEditDialog(student)} data-testid={`edit-student-${student.id}`} className="p-2 hover:bg-slate-700 rounded-md transition-colors text-blue-400 hover:text-blue-300">
            <Pencil size={16} weight="duotone" />
          </button>
          <button onClick={() => handleDelete(student.id)} data-testid={`delete-student-${student.id}`} className="p-2 hover:bg-slate-700 rounded-md transition-colors text-rose-400 hover:text-rose-300">
            <Trash size={16} weight="duotone" />
          </button>
        </div>
      </td>
    </tr>
  );

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full">
          <div className="text-slate-400">Loading...</div>
        </div>
      </Layout>
    );
  }

  const { siblingGroups, individuals } = getSiblingGroups();

  return (
    <Layout>
      <div className="p-6 sm:p-8 lg:p-12">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl sm:text-5xl font-black tracking-tighter text-slate-50 mb-2">
              Students
            </h1>
            <p className="text-base text-slate-400">Manage student registrations with face recognition</p>
          </div>

          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <button data-testid="add-student-button" className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white font-medium px-6 py-3 rounded-md transition-colors">
                <Plus size={20} weight="bold" />
                Add Student
              </button>
            </DialogTrigger>
            <DialogContent className="bg-slate-900 border-slate-800 text-slate-50 max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-2xl font-bold text-slate-50">Register New Student</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="block text-xs font-bold uppercase tracking-wider text-blue-500 mb-2">Capture Face</label>
                    <FaceCapture onCapture={handleFaceCapture} mode="register" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-blue-500 mb-2">Student Name</label>
                    <Input data-testid="student-name-input" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="bg-slate-950 border-slate-800 text-slate-50" required />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-blue-500 mb-2">Date of Birth</label>
                    <Input data-testid="student-dob-input" type="date" value={formData.date_of_birth} onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })} className="bg-slate-950 border-slate-800 text-slate-50" required />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-blue-500 mb-2">Joining Date</label>
                    <Input data-testid="student-joining-input" type="date" value={formData.joining_date} onChange={(e) => setFormData({ ...formData, joining_date: e.target.value })} className="bg-slate-950 border-slate-800 text-slate-50" required />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-blue-500 mb-2">Email (Optional)</label>
                    <Input data-testid="student-email-input" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="bg-slate-950 border-slate-800 text-slate-50" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-blue-500 mb-2">Phone</label>
                    <Input data-testid="student-phone-input" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className="bg-slate-950 border-slate-800 text-slate-50" required />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-blue-500 mb-2">Parent Name</label>
                    <Input data-testid="student-parent-name-input" value={formData.parent_name} onChange={(e) => setFormData({ ...formData, parent_name: e.target.value })} className="bg-slate-950 border-slate-800 text-slate-50" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-blue-500 mb-2">Parent Phone</label>
                    <Input data-testid="student-parent-phone-input" value={formData.parent_phone} onChange={(e) => setFormData({ ...formData, parent_phone: e.target.value })} className="bg-slate-950 border-slate-800 text-slate-50" />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs font-bold uppercase tracking-wider text-blue-500 mb-2">Batch</label>
                    <Select value={formData.batch_id} onValueChange={(value) => setFormData({ ...formData, batch_id: value })} required>
                      <SelectTrigger data-testid="student-batch-select" className="bg-slate-950 border-slate-800 text-slate-50"><SelectValue placeholder="Select batch" /></SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-800">
                        {batches.map((batch) => (
                          <SelectItem key={batch.id} value={batch.id} className="text-slate-50">{batch.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Button type="submit" data-testid="submit-student-button" className="w-full bg-blue-600 hover:bg-blue-500">Register Student</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* View Mode Toggle + Filters */}
        <div className="mb-6 flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setViewMode('list')}
              data-testid="view-list-btn"
              className={`flex items-center gap-2 px-4 py-2 rounded-md font-medium text-sm transition-colors ${
                viewMode === 'list' ? 'bg-blue-600 text-white' : 'bg-slate-900 text-slate-400 hover:bg-slate-800 border border-slate-800'
              }`}
            >
              <Users size={16} weight="bold" />
              All Students
            </button>
            <button
              onClick={() => setViewMode('siblings')}
              data-testid="view-siblings-btn"
              className={`flex items-center gap-2 px-4 py-2 rounded-md font-medium text-sm transition-colors ${
                viewMode === 'siblings' ? 'bg-blue-600 text-white' : 'bg-slate-900 text-slate-400 hover:bg-slate-800 border border-slate-800'
              }`}
            >
              <UsersThree size={16} weight="bold" />
              Siblings
            </button>
          </div>

          <div className="flex items-center gap-2 overflow-x-auto pb-2">
            <button onClick={() => setSelectedBatchFilter('all')} className={`px-4 py-2 rounded-md font-medium text-sm transition-colors whitespace-nowrap ${selectedBatchFilter === 'all' ? 'bg-blue-600 text-white' : 'bg-slate-900 text-slate-400 hover:bg-slate-800 border border-slate-800'}`} data-testid="filter-all-batches">All Batches</button>
            {batches.map((batch) => (
              <button key={batch.id} onClick={() => setSelectedBatchFilter(batch.id)} className={`px-4 py-2 rounded-md font-medium text-sm transition-colors whitespace-nowrap ${selectedBatchFilter === batch.id ? 'bg-blue-600 text-white' : 'bg-slate-900 text-slate-400 hover:bg-slate-800 border border-slate-800'}`} data-testid={`filter-batch-${batch.id}`}>{batch.name}</button>
            ))}
          </div>

          {viewMode === 'list' && (
            <div className="flex items-center gap-2 ml-auto">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search student..."
                data-testid="student-search-input"
                className="bg-slate-900 border border-slate-800 rounded-md px-4 py-2 text-sm text-slate-50 placeholder-slate-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 w-56"
              />
              <ArrowsDownUp size={18} weight="duotone" className="text-slate-400" />
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} data-testid="sort-students-select" className="bg-slate-900 border border-slate-800 rounded-md px-3 py-2 text-sm text-slate-50 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500">
                <option value="name_asc">Name A-Z</option>
                <option value="name_desc">Name Z-A</option>
                <option value="age_asc">Age: Youngest First</option>
                <option value="age_desc">Age: Oldest First</option>
                <option value="fees_pending">Fees Pending First</option>
              </select>
            </div>
          )}
        </div>

        {/* List View */}
        {viewMode === 'list' && (
          <>
            {students.length === 0 ? (
              <div className="bg-slate-900 border border-slate-800 rounded-lg p-12 text-center">
                <Users size={64} weight="duotone" className="mx-auto text-slate-700 mb-4" />
                <h3 className="text-xl font-semibold text-slate-300 mb-2">No students yet</h3>
                <p className="text-slate-500 mb-6">Register your first student to get started</p>
              </div>
            ) : (
              <div className="bg-slate-900 border border-slate-800 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-950 border-b border-slate-800">
                      <tr>
                        <th className="text-left px-4 py-4 text-xs font-bold uppercase tracking-wider text-slate-400">S.No</th>
                        <th className="text-left px-4 py-4 text-xs font-bold uppercase tracking-wider text-slate-400">Photo</th>
                        <th className="text-left px-4 py-4 text-xs font-bold uppercase tracking-wider text-slate-400">Name</th>
                        <th className="text-left px-4 py-4 text-xs font-bold uppercase tracking-wider text-slate-400">Age</th>
                        <th className="text-left px-4 py-4 text-xs font-bold uppercase tracking-wider text-slate-400">Batch</th>
                        <th className="text-left px-4 py-4 text-xs font-bold uppercase tracking-wider text-slate-400">Contact</th>
                        {showFees && <th className="text-left px-4 py-4 text-xs font-bold uppercase tracking-wider text-slate-400">Fee ({paymentStatus.month})</th>}
                        <th className="text-left px-4 py-4 text-xs font-bold uppercase tracking-wider text-slate-400">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800">
                      {getFilteredStudents().map((student, index) => renderStudentRow(student, index))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}

        {/* Siblings View */}
        {viewMode === 'siblings' && (
          <div className="space-y-6">
            {siblingGroups.length === 0 && individuals.length === 0 && (
              <div className="bg-slate-900 border border-slate-800 rounded-lg p-12 text-center">
                <UsersThree size={64} weight="duotone" className="mx-auto text-slate-700 mb-4" />
                <h3 className="text-xl font-semibold text-slate-300 mb-2">No siblings found</h3>
                <p className="text-slate-500">Students sharing the same parent phone number are grouped as siblings</p>
              </div>
            )}

            {siblingGroups.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold text-slate-50 mb-4">
                  <UsersThree size={20} weight="duotone" className="inline mr-2 text-blue-400" />
                  Sibling Groups ({siblingGroups.length})
                </h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {siblingGroups.map((group) => (
                    <div key={group.phone} data-testid={`sibling-group-${group.phone}`} className="bg-slate-900 border border-slate-800 rounded-lg overflow-hidden">
                      <div className="px-5 py-3 bg-slate-950 border-b border-slate-800 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <UsersThree size={16} weight="duotone" className="text-blue-400" />
                          <span className="text-sm font-medium text-slate-300">Siblings ({group.members.length})</span>
                        </div>
                        <span className="text-xs text-slate-500">Parent: {group.phone}</span>
                      </div>
                      <div className="divide-y divide-slate-800/50">
                        {group.members.map((student) => (
                          <div key={student.id} className="flex items-center justify-between px-5 py-3 hover:bg-slate-800/30">
                            <div className="flex items-center gap-3">
                              {student.photo_url ? (
                                <div className="w-9 h-9 rounded-full bg-blue-600/20 flex items-center justify-center border border-slate-700">
                                  <span className="text-blue-400 font-bold text-xs">{student.name?.charAt(0)?.toUpperCase()}</span>
                                </div>
                              ) : (
                                <div className="w-9 h-9 rounded-full bg-blue-600/20 flex items-center justify-center">
                                  <span className="text-xs font-bold text-blue-400">{student.name.charAt(0)}</span>
                                </div>
                              )}
                              <div>
                                <p className="text-sm font-medium text-slate-50">{student.name}</p>
                                <p className="text-xs text-slate-500">{getBatchName(student.batch_id)} {student.date_of_birth ? `| Age ${calculateAge(student.date_of_birth)}` : ''}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {showFees && (
                                isStudentTutor(student) ? (
                                  <span className="text-xs text-slate-500 italic">Tutor</span>
                                ) : isStudentPaid(student.id) ? (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-600/20 text-emerald-400 rounded-full text-xs">
                                    <CheckCircle size={10} weight="bold" /> Paid
                                  </span>
                                ) : (
                                  <button onClick={() => handleRecordPayment(student)} className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-600/20 text-amber-400 hover:bg-amber-600/30 rounded-full text-xs cursor-pointer transition-colors">
                                    <Clock size={10} weight="bold" /> Pending
                                  </button>
                                )
                              )}
                              <button onClick={() => openEditDialog(student)} className="p-1.5 hover:bg-slate-700 rounded-md transition-colors text-blue-400 hover:text-blue-300">
                                <Pencil size={14} weight="duotone" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                      {showFees && (
                        <div className="px-5 py-2 bg-slate-950 border-t border-slate-800">
                          <span className="text-xs text-slate-500">
                            <CurrencyDollar size={12} weight="bold" className="inline mr-1" />
                            Sibling rate: KWD 35 for {group.members.length} students
                          </span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {individuals.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold text-slate-50 mb-4">
                  <Users size={20} weight="duotone" className="inline mr-2 text-slate-400" />
                  Individual Students ({individuals.length})
                </h2>
                <div className="bg-slate-900 border border-slate-800 rounded-lg overflow-hidden">
                  <div className="divide-y divide-slate-800/50">
                    {individuals.map((student, idx) => (
                      <div key={student.id} className="flex items-center justify-between px-5 py-3 hover:bg-slate-800/30">
                        <div className="flex items-center gap-3">
                          <span className="text-xs text-slate-500 w-6 text-right">{idx + 1}.</span>
                          {student.photo_url ? (
                            <div className="w-9 h-9 rounded-full bg-blue-600/20 flex items-center justify-center border border-slate-700">
                              <span className="text-blue-400 font-bold text-xs">{student.name?.charAt(0)?.toUpperCase()}</span>
                            </div>
                          ) : (
                            <div className="w-9 h-9 rounded-full bg-blue-600/20 flex items-center justify-center">
                              <span className="text-xs font-bold text-blue-400">{student.name.charAt(0)}</span>
                            </div>
                          )}
                          <div>
                            <p className="text-sm font-medium text-slate-50">{student.name}</p>
                            <p className="text-xs text-slate-500">{getBatchName(student.batch_id)}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {showFees && (
                            isStudentTutor(student) ? (
                              <span className="text-xs text-slate-500 italic">Tutor</span>
                            ) : isStudentPaid(student.id) ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-600/20 text-emerald-400 rounded-full text-xs">
                                <CheckCircle size={10} weight="bold" /> Paid
                              </span>
                            ) : (
                              <button onClick={() => handleRecordPayment(student)} className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-600/20 text-amber-400 hover:bg-amber-600/30 rounded-full text-xs cursor-pointer transition-colors">
                                <Clock size={10} weight="bold" /> Pending
                              </button>
                            )
                          )}
                          <button onClick={() => openEditDialog(student)} className="p-1.5 hover:bg-slate-700 rounded-md transition-colors text-blue-400 hover:text-blue-300">
                            <Pencil size={14} weight="duotone" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-50 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-slate-50">Edit Student</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEdit} className="space-y-4 mt-4">
            <div className="col-span-2">
              <label className="block text-xs font-bold uppercase tracking-wider text-blue-500 mb-2">Update Photo</label>
              <FaceCapture onCapture={handleEditFaceCapture} mode="register" />
              {editFaceData && <p className="text-sm text-emerald-400 mt-2">New photo captured</p>}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-blue-500 mb-2">Student Name</label>
                <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="bg-slate-950 border-slate-800 text-slate-50" required />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-blue-500 mb-2">Date of Birth</label>
                <Input type="date" value={formData.date_of_birth} onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })} className="bg-slate-950 border-slate-800 text-slate-50" />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-blue-500 mb-2">Joining Date</label>
                <Input type="date" value={formData.joining_date} onChange={(e) => setFormData({ ...formData, joining_date: e.target.value })} className="bg-slate-950 border-slate-800 text-slate-50" />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-blue-500 mb-2">Email</label>
                <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="bg-slate-950 border-slate-800 text-slate-50" />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-blue-500 mb-2">Phone</label>
                <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className="bg-slate-950 border-slate-800 text-slate-50" />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-blue-500 mb-2">Parent Name</label>
                <Input value={formData.parent_name} onChange={(e) => setFormData({ ...formData, parent_name: e.target.value })} className="bg-slate-950 border-slate-800 text-slate-50" />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-blue-500 mb-2">Parent Phone</label>
                <Input value={formData.parent_phone} onChange={(e) => setFormData({ ...formData, parent_phone: e.target.value })} className="bg-slate-950 border-slate-800 text-slate-50" />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-blue-500 mb-2">Batch</label>
                <Select value={formData.batch_id} onValueChange={(value) => setFormData({ ...formData, batch_id: value })}>
                  <SelectTrigger className="bg-slate-950 border-slate-800 text-slate-50"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800">
                    {batches.map((batch) => (
                      <SelectItem key={batch.id} value={batch.id} className="text-slate-50">{batch.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-500">Update Student</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Student History Dialog */}
      <Dialog open={isHistoryOpen} onOpenChange={setIsHistoryOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-50 max-w-2xl max-h-[85vh] overflow-y-auto">
          {historyLoading ? (
            <div className="text-center py-12 text-slate-400">Loading history...</div>
          ) : historyData ? (
            <>
              <DialogHeader>
                <DialogTitle className="text-xl font-bold flex items-center gap-3">
                  {historyData.student?.photo_url ? (
                    <img src={historyData.student.photo_url} alt="" className="w-12 h-12 rounded-full object-cover border-2 border-blue-500" />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-blue-600/20 flex items-center justify-center">
                      <Users size={24} weight="duotone" className="text-blue-400" />
                    </div>
                  )}
                  <div>
                    <div>{historyData.student?.name}</div>
                    <div className="text-sm font-normal text-slate-400">
                      {getBatchName(historyData.student?.batch_id)} | {historyData.student?.phone}
                    </div>
                  </div>
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-6 mt-4">
                {/* Absence History */}
                {historyData.absence_history?.length > 0 && (
                  <div>
                    <h3 className="text-sm font-bold uppercase tracking-wider text-rose-400 mb-3 flex items-center gap-2">
                      <Clock size={16} weight="bold" />
                      Absent Months
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {historyData.absence_history.map((abs, idx) => {
                        const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
                        const monthStr = typeof abs.month === 'number'
                          ? monthNames[abs.month - 1]
                          : abs.month;
                        return (
                          <span key={idx} className="px-3 py-1 bg-rose-600/20 text-rose-400 rounded-full text-xs font-medium border border-rose-500/30">
                            {monthStr} {abs.year}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Monthly Attendance */}
                <div>
                  <h3 className="text-sm font-bold uppercase tracking-wider text-blue-400 mb-3 flex items-center gap-2">
                    <CalendarDots size={16} weight="bold" />
                    Monthly Attendance
                  </h3>
                  {historyData.attendance_history?.length > 0 ? (
                    <div className="bg-slate-950 rounded-lg border border-slate-800 overflow-hidden">
                      <table className="w-full">
                        <thead className="border-b border-slate-800">
                          <tr>
                            <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">Month</th>
                            <th className="text-right px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">Days Present</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-800">
                          {historyData.attendance_history.map((att, idx) => {
                            const [year, month] = att.month.split('-');
                            const monthName = new Date(parseInt(year), parseInt(month) - 1).toLocaleString('default', { month: 'long', year: 'numeric' });
                            return (
                              <tr key={idx} className="hover:bg-slate-800/50">
                                <td className="px-4 py-3 text-slate-300 text-sm">{monthName}</td>
                                <td className="px-4 py-3 text-right">
                                  <span className={`font-semibold ${att.days_present >= 4 ? 'text-emerald-400' : 'text-amber-400'}`}>
                                    {att.days_present} days
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-slate-500 text-sm py-3">No attendance records yet</p>
                  )}
                </div>

                {/* Payment History */}
                <div>
                  <h3 className="text-sm font-bold uppercase tracking-wider text-emerald-400 mb-3 flex items-center gap-2">
                    <Receipt size={16} weight="bold" />
                    Fee Payment History
                  </h3>
                  {historyData.payment_history?.length > 0 ? (
                    <div className="bg-slate-950 rounded-lg border border-slate-800 overflow-hidden">
                      <table className="w-full">
                        <thead className="border-b border-slate-800">
                          <tr>
                            <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">Month</th>
                            <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">Amount</th>
                            <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">Method</th>
                            <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">Date</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-800">
                          {historyData.payment_history.map((pay, idx) => (
                            <tr key={idx} className="hover:bg-slate-800/50">
                              <td className="px-4 py-3 text-slate-300 text-sm">{pay.month} {pay.year}</td>
                              <td className="px-4 py-3 text-emerald-400 font-semibold text-sm">KWD {pay.amount}</td>
                              <td className="px-4 py-3 text-slate-400 text-sm capitalize">{pay.payment_method}</td>
                              <td className="px-4 py-3 text-slate-400 text-sm">{pay.payment_date ? new Date(pay.payment_date).toLocaleDateString() : '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-slate-500 text-sm py-3">No payment records yet</p>
                  )}
                </div>
              </div>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

export default Students;
